import { describeMutationError } from "@api/mutationErrors";
import { useSessionKey } from "@auth/sessionStore";
import { boardName } from "@board/VisitCard";
import { useUser } from "@context/UserContext";
import {
  type FixtureBox,
  ROOM_KIND_LABEL,
  type Room,
  type RoomKind,
} from "@rooms/roomsClient";
import {
  useClearRoomBaseline,
  useRoomBaseline,
  useRoomsListQuery,
  useUpdateRoom,
} from "@rooms/roomsQueries";
import {
  Button,
  Chip,
  cn,
  CompressIcon,
  CheckIcon,
  ConfirmDialog,
  Dropdown,
  EmptyNote,
  FieldError,
  ExpandIcon,
  IconButton,
  LabelTape,
  PanelError,
  SegmentedControl,
  SkeletonStack,
  Tag,
  Toggle,
} from "@scribenote/brian-design-system";
import { isLeadership } from "@users/userClient";
import { useTodayVisitsQuery } from "@visits/visitsQueries";
import { type ReactNode, type Ref, useEffect, useRef, useState } from "react";

import { BaselineCapture } from "./BaselineCapture";
import { CorrectDrawer } from "./CorrectDrawer";
import type { SourceSize } from "./fixtureBoxes";
import { GROUND_TRUTH_KEY, GroundTruth } from "./GroundTruth";
import { LiveVideo } from "./LiveVideo";
import { MaskControls, MaskOverlay } from "./MaskEditor";
import { parseMs, STALE_IMAGE_MS, useNow } from "./monitorTime";
import {
  isManual,
  type RoomOccupant,
  type RoomPhase,
  type RoomStill,
} from "./perceptionClient";
import {
  useRoomPhases,
  useRoomStills,
  useRoomVideo,
  VIDEO_MS,
} from "./perceptionQueries";
import { frameTime, RecentVideo, VideoFrom } from "./RecentVideo";
import { RoomEvents } from "./RoomEvents";
import { RoomFullScreen } from "./RoomFullScreen";
import { SlotPanel } from "./SlotPanel";
import { StaffPhotoCapture } from "./StaffPhotoCapture";
import { DEFAULT_ASPECT, StillFrame } from "./StillFrame";
import { StillViewer } from "./StillViewer";
import {
  ago,
  countWord,
  countsWord,
  phaseWord,
  visitWord,
  whoWord,
} from "./words";

interface Shown {
  capturedAt: string;
  url: string;
  reference: string;
  size: SourceSize;
  failed: boolean;
}

const MAX_PRELOADS = 3;

const isNewer = (still: RoomStill, shown: Shown | null) =>
  !shown || (parseMs(still.capturedAt) ?? 0) > (parseMs(shown.capturedAt) ?? 0);

/**
 * brian signs the still again on every read, so a new URL is not a new still:
 * `capturedAt` decides. A new still preloads off screen and replaces the shown
 * one once it has loaded, so the picture does not blank between polls.
 */
const useShownStill = (still: RoomStill | null | undefined) => {
  const [shown, setShown] = useState<Shown | null>(null);
  const loading = useRef(new Set<string>());
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const shownAt = shown?.capturedAt;
  useEffect(() => {
    if (!still || still.capturedAt === shownAt) return;
    const key = still.capturedAt;
    if (loading.current.has(key) || loading.current.size >= MAX_PRELOADS)
      return;
    loading.current.add(key);
    const preload = new Image();
    const settle = (failed: boolean) => {
      loading.current.delete(key);
      if (!mounted.current) return;
      setShown((prev) =>
        isNewer(still, prev)
          ? {
              capturedAt: key,
              url: still.url,
              reference: still.reference,
              size: {
                width: still.width ?? preload.naturalWidth,
                height: still.height ?? preload.naturalHeight,
              },
              failed,
            }
          : prev,
      );
    };
    preload.referrerPolicy = "no-referrer";
    preload.onload = () => settle(false);
    preload.onerror = () => settle(true);
    preload.src = still.url;
  }, [still, shownAt]);

  /** A still left on screen outlives its presigned URL; retry once with the latest signature. */
  const onImageError = () => {
    if (!shown) return;
    const latest =
      still && still.capturedAt === shown.capturedAt ? still.url : undefined;
    setShown(
      latest && latest !== shown.url
        ? { ...shown, url: latest }
        : { ...shown, failed: true },
    );
  };

  return { shown, onImageError };
};

const unidentifiedWord = (occupants: readonly RoomOccupant[]) => {
  const people = occupants.filter((o) => o.kind === "person").length;
  const animals = occupants.length - people;
  return [
    people &&
      countWord(String(people), "unidentified person", "unidentified people"),
    animals &&
      countWord(String(animals), "unidentified animal", "unidentified animals"),
  ]
    .filter(Boolean)
    .join(" · ");
};

const RosterEntry = ({
  slot,
  onOpen,
  children,
}: {
  slot: string;
  onOpen?: (slot: string) => void;
  children: ReactNode;
}) =>
  onOpen ? (
    <button
      type="button"
      title={`Why Brian shows ${slot} this way`}
      onClick={() => onOpen(slot)}
      className="flex flex-wrap items-baseline gap-1.5 rounded-paper text-left underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-accent"
    >
      {children}
    </button>
  ) : (
    <span className="flex flex-wrap items-baseline gap-1.5">{children}</span>
  );

/** Names patients, so it is hidden from RUM. Slot ids say nothing to staff, so
 * the unidentified are a count (BRI-337). With `onOpen` (leaders), an entry opens
 * its slot panel and the count opens the first unidentified slot (BRI-354). */
const RosterLine = ({
  roomName,
  occupants,
  patients,
  now,
  onOpen,
  className,
}: {
  roomName: string;
  occupants: readonly RoomOccupant[];
  patients: ReadonlyMap<string, string>;
  now: number;
  onOpen?: (slot: string) => void;
  className?: string;
}) => {
  const current = occupants.filter((o) => o.misses === 0);
  const retained = occupants.filter((o) => o.misses > 0);
  const occupantLine = (o: RoomOccupant) => {
    const visit = visitWord(o, patients);
    const unlinked = !visit && o.role === "client";
    return (
      <li key={o.slot}>
        <RosterEntry slot={o.slot} onOpen={onOpen}>
          <span className={cn(o.displayName && "font-semibold")}>
            {whoWord(o)}
          </span>
          {visit && <span>→ {visit}</span>}
          {unlinked && <span className="text-annotation">no visit</span>}
          {isManual(o) && <span className="text-annotation">manual</span>}
          {o.misses > 0 && (
            <span className="text-annotation">
              last seen {ago(o.lastSeenAt, now) ?? "at an unknown time"}
            </span>
          )}
        </RosterEntry>
      </li>
    );
  };
  const occupantLines = (group: readonly RoomOccupant[]) => {
    const unknown = group.filter((o) => !o.identified);
    return (
      <>
        {group.filter((o) => o.identified).map(occupantLine)}
        {unknown.length > 0 && (
          <li className="text-annotation">
            <RosterEntry slot={unknown[0].slot} onOpen={onOpen}>
              {unidentifiedWord(unknown)}
            </RosterEntry>
          </li>
        )}
      </>
    );
  };
  return (
    <div
      data-dd-privacy="hidden"
      className={cn("px-3 py-1.5 text-sm text-ink-body", className)}
    >
      <ul
        aria-label={`In view in ${roomName}`}
        className="flex flex-wrap gap-x-4 gap-y-1"
      >
        {current.length === 0 ? (
          <li className="text-annotation">No occupants currently in view</li>
        ) : (
          occupantLines(current)
        )}
      </ul>
      {retained.length > 0 && (
        <details className="mt-1 text-annotation">
          <summary className="cursor-pointer">
            Out of view ({retained.length})
          </summary>
          <p className="mt-1 text-fine-print">
            Previously seen people and animals retained while out of view.
          </p>
          <ul
            aria-label={`Out of view in ${roomName}`}
            className="mt-1 flex flex-col gap-1"
          >
            {occupantLines(retained)}
          </ul>
        </details>
      )}
    </div>
  );
};

/** Brian's baseline URL is short-lived, so it is fetched only while shown. */
const BaselineStatus = ({ room, now }: { room: Room; now: number }) => {
  const sessionKey = useSessionKey();
  const [showing, setShowing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const clear = useClearRoomBaseline();
  const baseline = useRoomBaseline(sessionKey, room.id, showing);

  if (room.cameraBaselineAt === null)
    return (
      <p className="border-t border-rule px-3 py-1.5 text-sm text-annotation">
        No empty-room baseline.
      </p>
    );
  return (
    <div className="flex flex-col gap-1.5 border-t border-rule px-3 py-1.5 text-sm text-ink-body">
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
        <span>Baseline set {ago(room.cameraBaselineAt, now) ?? "earlier"}</span>
        <span aria-hidden>·</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowing((open) => !open)}
        >
          {showing ? "Hide" : "Show"}
        </Button>
        <span aria-hidden>·</span>
        <Button variant="ghost" size="sm" onClick={() => setConfirming(true)}>
          Clear
        </Button>
      </div>
      {showing &&
        (baseline.isFetching ? (
          <span className="text-annotation">Loading the baseline.</span>
        ) : (
          baseline.data && (
            <div className="w-48 max-w-full">
              <StillFrame url={baseline.data.url} />
            </div>
          )
        ))}
      <ConfirmDialog
        open={confirming}
        title={`Clear the baseline for ${room.name}?`}
        confirmLabel="Clear"
        destructive
        busy={clear.isPending}
        onCancel={() => {
          clear.reset();
          setConfirming(false);
        }}
        onConfirm={() =>
          clear.mutate(room.id, {
            onSuccess: () => {
              setConfirming(false);
              setShowing(false);
            },
          })
        }
      >
        <p className="text-sm text-ink-body">The saved image is deleted.</p>
        {clear.isError && (
          <FieldError className="mt-2">
            {describeMutationError(clear.error, "clear the baseline")}
          </FieldError>
        )}
      </ConfirmDialog>
    </div>
  );
};

interface StillQuery {
  data: RoomStill | null | undefined;
  isError: boolean;
}

type CameraView =
  | { kind: "live" }
  | { kind: "stream" }
  | { kind: "video"; from: number };

/** One room's newest still and committed phase; ages are on the browser's clock. */
const CameraFeed = ({
  room,
  stillQuery,
  phase,
  patients,
  active,
  now,
  truthRef,
  fullScreen,
  onFullScreen,
  onPointerEnter,
}: {
  room: Room;
  stillQuery: StillQuery | undefined;
  phase: RoomPhase | null;
  patients: ReadonlyMap<string, string>;
  active: boolean;
  now: number;
  truthRef: Ref<HTMLInputElement>;
  fullScreen: boolean;
  onFullScreen: (on: boolean) => void;
  onPointerEnter: () => void;
}) => {
  const { shown, onImageError } = useShownStill(stillQuery?.data);
  const [draft, setDraft] = useState<readonly FixtureBox[] | null>(null);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const [baselineStill, setBaselineStill] = useState<Shown | null>(null);
  const [viewing, setViewing] = useState(false);
  const [explaining, setExplaining] = useState<string | null>(null);
  const [cameraView, setCameraView] = useState<CameraView>({ kind: "live" });
  if (!fullScreen && cameraView.kind !== "live")
    setCameraView({ kind: "live" });
  const videoFrom = cameraView.kind === "video" ? cameraView.from : null;
  const sessionKey = useSessionKey();
  const video = useRoomVideo(sessionKey, room.id, videoFrom);
  const videoRef = useRef<HTMLVideoElement>(null);
  const recorded = video.data;
  const leader = isLeadership(useUser().currentUser);
  const expandButton = useRef<HTMLButtonElement>(null);
  const wasFullScreen = useRef(fullScreen);
  useEffect(() => {
    if (wasFullScreen.current && !fullScreen) expandButton.current?.focus();
    wasFullScreen.current = fullScreen;
  }, [fullScreen]);
  const still =
    shown && !shown.failed && shown.size.width > 0 && shown.size.height > 0
      ? shown
      : null;
  const masking = !!still && draft !== null;
  // Closed, not just hidden: left set, the viewer would reopen by itself when a
  // still came back, possibly minutes later.
  if (viewing && (!still || masking)) setViewing(false);
  const capturedMs = parseMs(shown?.capturedAt ?? null);
  const stale = capturedMs === null || now - capturedMs > STALE_IMAGE_MS;
  const countsMs = parseMs(phase?.counts?.at ?? null);
  const countsStale = countsMs === null || now - countsMs > STALE_IMAGE_MS;
  const aspect = still ? still.size.width / still.size.height : DEFAULT_ASPECT;

  const title = (
    <>
      <LabelTape className="max-w-[12rem] shrink-0 truncate">
        {room.name}
      </LabelTape>
      {phase ? (
        <>
          <Chip tone="brian" size="sm">
            {phaseWord(phase.phase)}
          </Chip>
          <span className="min-w-0 truncate font-mono text-fine-print text-annotation">
            since {ago(phase.since, now)}
          </span>
        </>
      ) : (
        <span className="min-w-0 truncate text-sm text-annotation">
          No phase yet
        </span>
      )}
    </>
  );

  const counts = phase?.counts && (
    <span
      className={cn(
        "min-w-0 truncate font-mono text-fine-print text-annotation",
        countsStale && "opacity-60",
      )}
    >
      {countsWord(phase.counts)} · {ago(phase.counts.at, now)}
    </span>
  );

  const status =
    shown &&
    (stale ? (
      <Tag tone="emphasis">Stale</Tag>
    ) : (
      <Chip tone="recorded" size="sm">
        Live
      </Chip>
    ));

  const actions = (
    <>
      <RoomKindMenu room={room} />
      {still && !masking && (
        <>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDraft(room.cameraFixtures ?? [])}
          >
            Edit mask
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setPhotoOpen(true)}>
            Reference photo
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setBaselineStill(still)}
          >
            Empty-room baseline
          </Button>
        </>
      )}
      {/* Not gated on a still: a room whose camera is down still needs its roster corrected. */}
      <Button variant="ghost" size="sm" onClick={() => setCorrecting(true)}>
        Fix who's here
      </Button>
    </>
  );

  const roster = phase && (
    <RosterLine
      roomName={room.name}
      occupants={phase.occupants}
      patients={patients}
      now={now}
      onOpen={leader ? setExplaining : undefined}
      className={fullScreen ? undefined : "border-b border-rule"}
    />
  );

  const picture = (
    <div
      data-dd-privacy="hidden"
      className="relative w-full overflow-hidden bg-recess"
      style={{
        aspectRatio: aspect,
        // Fits both ways inside the size container below.
        width: fullScreen ? `min(100cqw, 100cqh * ${aspect})` : undefined,
      }}
    >
      {still ? (
        <>
          <img
            src={still.url}
            alt={`Last still from ${room.name}`}
            referrerPolicy="no-referrer"
            onError={onImageError}
            className={cn(
              "absolute inset-0 h-full w-full object-fill",
              stale && "opacity-60",
            )}
          />
          {masking && (
            <MaskOverlay
              size={still.size}
              fixtures={draft}
              onChange={setDraft}
            />
          )}
          <span className="absolute bottom-1 left-1 rounded-paper bg-card px-1 font-mono text-fine-print text-ink-body">
            Last image: {ago(still.capturedAt, now)}
          </span>
          {!masking && (
            <button
              type="button"
              aria-label={`View full size, ${still.size.width}×${still.size.height}`}
              onClick={() => setViewing(true)}
              className="absolute right-1 bottom-1 flex items-center gap-1.5 rounded-paper bg-card px-1.5 py-1 font-mono text-fine-print text-ink-body transition duration-150 ease-paper hover:bg-smudge focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            >
              {still.size.width}×{still.size.height}
              <ExpandIcon />
            </button>
          )}
        </>
      ) : (
        <p className="absolute inset-0 flex items-center justify-center p-4 text-center text-sm text-annotation">
          {stillQuery?.isError
            ? "I couldn’t load the still."
            : stillQuery?.data === null
              ? "No still from this room yet."
              : shown?.failed
                ? "I couldn’t load the last still."
                : "Loading the still."}
        </p>
      )}
    </div>
  );

  const baseline = <BaselineStatus room={room} now={now} />;

  const truth = (
    <GroundTruth
      roomId={room.id}
      active={active}
      doorTaps={fullScreen}
      now={now}
      inputRef={truthRef}
      at={
        recorded
          ? () => frameTime(recorded, videoRef.current?.currentTime ?? 0)
          : undefined
      }
    />
  );

  const emitted = <RoomEvents roomId={room.id} active={active} now={now} />;

  const maskControls = masking && (
    <MaskControls
      room={room}
      fixtures={draft}
      onChange={setDraft}
      onDone={() => setDraft(null)}
    />
  );

  const dialogs = (
    <>
      {photoOpen && (
        <StaffPhotoCapture
          roomId={room.id}
          onClose={() => setPhotoOpen(false)}
        />
      )}

      {correcting && (
        <CorrectDrawer
          roomId={room.id}
          roomName={room.name}
          occupants={phase?.occupants ?? []}
          now={now}
          onClose={() => setCorrecting(false)}
        />
      )}

      {baselineStill && (
        <BaselineCapture
          roomId={room.id}
          url={baselineStill.url}
          reference={baselineStill.reference}
          onClose={() => setBaselineStill(null)}
        />
      )}

      {viewing && still && (
        <StillViewer
          roomName={room.name}
          url={still.url}
          size={still.size}
          capturedAt={still.capturedAt}
          now={now}
          onClose={() => setViewing(false)}
        />
      )}

      {explaining && (
        <SlotPanel
          roomId={room.id}
          roomName={room.name}
          occupants={phase?.occupants ?? []}
          patients={patients}
          slot={explaining}
          now={now}
          onSelect={setExplaining}
          onClose={() => setExplaining(null)}
        />
      )}
    </>
  );

  const view = fullScreen ? (
    <RoomFullScreen roomName={room.name} onClose={() => onFullScreen(false)}>
      <header className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-rule bg-card px-4 py-2">
        <div className="flex min-w-0 items-center gap-2">{title}</div>
        {counts}
        {status}
        <div className="ml-auto flex flex-wrap items-center gap-1">
          {actions}
        </div>
        <IconButton
          size="sm"
          aria-label="Exit full screen"
          title="Exit full screen (Esc)"
          onClick={() => onFullScreen(false)}
        >
          <CompressIcon />
        </IconButton>
      </header>
      <div className="flex min-h-0 flex-1 flex-col md:flex-row">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <SegmentedControl
              aria-label="Camera view"
              size="sm"
              options={[
                { value: "live", label: "Live" },
                { value: "stream", label: "Live video" },
                { value: "video", label: "Recent video" },
              ]}
              value={cameraView.kind}
              onValueChange={(kind) =>
                setCameraView(
                  kind === "video"
                    ? { kind, from: Date.now() - VIDEO_MS }
                    : { kind },
                )
              }
            />
            {cameraView.kind === "video" && (
              <VideoFrom
                from={cameraView.from}
                onFrom={(from) => setCameraView({ kind: "video", from })}
              />
            )}
          </div>
          <div
            className="flex min-h-0 min-w-0 flex-1 items-center justify-center"
            style={{ containerType: "size" }}
          >
            {cameraView.kind === "stream" ? (
              <LiveVideo
                roomId={room.id}
                roomName={room.name}
                aspect={aspect}
              />
            ) : cameraView.kind === "live" ? (
              picture
            ) : (
              <RecentVideo
                roomName={room.name}
                query={video}
                videoRef={videoRef}
                aspect={aspect}
              />
            )}
          </div>
        </div>
        <aside
          aria-label={`${room.name} controls`}
          className="flex max-h-[45%] shrink-0 flex-col overflow-y-auto border-t border-rule bg-card md:max-h-none md:w-96 md:border-t-0 md:border-l"
        >
          {roster}
          {truth}
          {emitted}
          {maskControls}
          {baseline}
        </aside>
      </div>
    </RoomFullScreen>
  ) : (
    <article
      aria-label={`Camera in ${room.name}`}
      className="flex min-w-0 flex-col overflow-hidden rounded-paper border border-rule bg-card"
      onPointerEnter={onPointerEnter}
    >
      <header className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-rule px-3 py-2">
        <div className="flex min-w-0 basis-full items-center gap-2">
          {title}
          <div className="ml-auto flex shrink-0 items-center gap-1">
            {status}
            <IconButton
              ref={expandButton}
              size="sm"
              aria-label={`Full screen ${room.name}`}
              title="Full screen"
              onClick={() => onFullScreen(true)}
            >
              <ExpandIcon />
            </IconButton>
          </div>
        </div>
        {counts && <div className="flex min-w-0 basis-full">{counts}</div>}
        <div className="ml-auto flex shrink-0 items-center gap-1">
          {actions}
        </div>
      </header>

      {roster}

      {picture}

      {baseline}

      {truth}

      {emitted}

      {maskControls}
    </article>
  );

  return (
    <>
      {view}
      {/* Outside the layout switch, so changing it keeps an open dialog and its unsaved edits. */}
      {dialogs}
    </>
  );
};

const RoomKindMenu = ({ room }: { room: Room }) => {
  const update = useUpdateRoom();
  return (
    <Dropdown
      items={Object.entries(ROOM_KIND_LABEL).map(([kind, label]) => ({
        id: kind,
        label,
        trailing:
          kind === room.kind ? (
            <CheckIcon aria-hidden="true" className="size-3.5" />
          ) : undefined,
      }))}
      onSelect={(kind) => {
        if (kind !== room.kind)
          update.mutate({
            roomUuid: room.id,
            change: { kind: kind as RoomKind },
          });
      }}
      trigger={(props) => (
        <Button
          {...props}
          variant="ghost"
          size="sm"
          disabled={update.isPending}
          aria-label={`Kind of ${room.name}: ${ROOM_KIND_LABEL[room.kind]}`}
          data-dd-action-name="Room kind"
        >
          {ROOM_KIND_LABEL[room.kind]}
        </Button>
      )}
    />
  );
};

/**
 * Each active room's newest still and phase. Its writes are a room's mask
 * (`PUT /rooms/{room}/camera`) and a tester's ground truth; reference photos,
 * roster corrections and the empty-room baseline are saved from their
 * dialogs. L puts the cursor in the ground-truth box of the room under the
 * pointer. It polls only while this tab is selected and the page is visible.
 */
export const CameraFeedPanel = ({
  active,
  fullScreenRoom,
  onFullScreenRoom,
}: {
  active: boolean;
  fullScreenRoom: string | undefined;
  onFullScreenRoom: (roomId: string | undefined) => void;
}) => {
  const sessionKey = useSessionKey();
  const rooms = useRoomsListQuery(sessionKey, true, active);
  const stills = useRoomStills(
    sessionKey,
    rooms.data?.map((room) => room.id) ?? [],
    active,
  );
  const phases = useRoomPhases(sessionKey, active);
  const visits = useTodayVisitsQuery(sessionKey, active);
  const now = useNow(active);
  const [feedsOnly, setFeedsOnly] = useState(true);
  const truthInputs = useRef(new Map<string, HTMLInputElement>());
  const pointedRoom = useRef<string | null>(null);

  useEffect(() => {
    if (!active) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== GROUND_TRUTH_KEY) return;
      if (e.repeat || e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target;
      if (
        target instanceof HTMLElement &&
        (["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName) ||
          target.isContentEditable)
      )
        return;
      const roomId =
        [fullScreenRoom, pointedRoom.current].find(
          (id) => !!id && truthInputs.current.has(id),
        ) ?? truthInputs.current.keys().next().value;
      const input = roomId ? truthInputs.current.get(roomId) : undefined;
      if (!input) return;
      e.preventDefault();
      input.focus();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [active, fullScreenRoom]);

  if (!rooms.data) {
    if (rooms.isError)
      return (
        <PanelError label="the rooms" onRetry={() => void rooms.refetch()} />
      );
    return <SkeletonStack count={2} itemClassName="h-48" />;
  }
  if (rooms.data.length === 0)
    return <EmptyNote className="p-6">No active rooms.</EmptyNote>;

  const phaseOf = new Map(phases.data?.map((p) => [p.roomId, p]));
  const patients = new Map(visits.data?.map((v) => [v.id, boardName(v)]));
  const feeds = rooms.data.map((room, i) => ({ room, stillQuery: stills[i] }));
  const showingFullScreen = feeds.some(
    ({ room }) => room.id === fullScreenRoom,
  );
  // Not while a room is full screen: the skeleton would unmount it.
  if (
    feedsOnly &&
    !showingFullScreen &&
    feeds.some(({ stillQuery }) => stillQuery?.isPending)
  )
    return <SkeletonStack count={2} itemClassName="h-48" />;
  // Has sent a still. Not the Live chip, which a camera stalled for 30 s loses; not
  // `captureEnabled`, which stays null until someone toggles capture. The
  // full-screen room stays in even without one, or its view would not mount.
  const shownFeeds = feedsOnly
    ? feeds.filter(
        ({ room, stillQuery }) =>
          stillQuery?.data !== null || room.id === fullScreenRoom,
      )
    : feeds;
  return (
    <div className="flex flex-col gap-3 font-body">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Toggle
          checked={feedsOnly}
          onCheckedChange={setFeedsOnly}
          label="Only rooms with a camera feed"
        />
        <span className="text-sm text-annotation">
          {shownFeeds.length} of {rooms.data.length} rooms
        </span>
      </div>
      {shownFeeds.length === 0 ? (
        <EmptyNote className="p-6">
          No room has sent a camera still yet.
        </EmptyNote>
      ) : (
        <div
          data-testid="perception-camera-feeds"
          className="grid grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3"
        >
          {shownFeeds.map(({ room, stillQuery }) => (
            <CameraFeed
              key={room.id}
              room={room}
              stillQuery={stillQuery}
              phase={phaseOf.get(room.id) ?? null}
              patients={patients}
              active={active}
              now={now}
              truthRef={(input) => {
                if (input) truthInputs.current.set(room.id, input);
                else truthInputs.current.delete(room.id);
              }}
              fullScreen={room.id === fullScreenRoom}
              onFullScreen={(on) => onFullScreenRoom(on ? room.id : undefined)}
              onPointerEnter={() => {
                pointedRoom.current = room.id;
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};
